import { useEligibleForTwcg1935CodingAgentChoice } from '@/components/user-context/helpers/cohorts.ts';
import { experiments } from '@/utils/experiment/manifest.ts';
import { getExperiment, useExperiment } from '@/utils/experiment/use-experiment.ts';

import {
	useEligibleForSimplifiedLandingViewWithBasicTasks,
	useEligibleForBasicSampleTasksForScrumOrSoftwareKanban,
	useEligibleForSampleTasksForLandingGateNonTech,
} from '../controllers';
import { WizardTemplateOption } from '../utils/types';

export type ExperimentKeys = keyof typeof experiments;

export const useIsJFindInviteStepExperiment = () => {
	const jfindInviteStepExperimentConfig = getExperiment(
		experiments.jfindOnboardingWizardInviteStep,
	);
	return (
		jfindInviteStepExperimentConfig.type === 'enrolled' && jfindInviteStepExperimentConfig.value
	);
};

export const useIsSimplifiedLandingViewWithBasicTasks = (template: WizardTemplateOption) => {
	const simplifiedListViewWithBasicTasksExperimentConfig = useExperiment(
		experiments.jfindSimplifiedListViewWithBasicTasks,
	);
	const { userEligibleForSimplifiedLandingView } =
		useEligibleForSimplifiedLandingViewWithBasicTasks(template);
	if (!userEligibleForSimplifiedLandingView) {
		return {
			simplifiedLandingViewWithBasicTasksIsEnrolled: false,
			simplifiedLandingViewWithBasicTasksFireExposure: undefined,
		};
	}

	const simplifiedLandingViewWithBasicTasksIsEnrolled =
		simplifiedListViewWithBasicTasksExperimentConfig.type === 'enrolled';

	const simplifiedLandingViewWithBasicTasksFireExposure =
		simplifiedLandingViewWithBasicTasksIsEnrolled
			? simplifiedListViewWithBasicTasksExperimentConfig.fireExposure
			: undefined;

	return {
		simplifiedLandingViewWithBasicTasksIsEnrolledInTest:
			simplifiedLandingViewWithBasicTasksIsEnrolled &&
			simplifiedListViewWithBasicTasksExperimentConfig.value,
		simplifiedLandingViewWithBasicTasksFireExposure,
	};
};

export const useIsBasicSampleTasksForScrumOrSoftwareKanban = () => {
	const basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig = useExperiment(
		experiments.jfindBasicSampleTasksForScrumOrSoftwareKanban,
	);

	const { userEligibleForBasicSampleTasksForScrumOrSoftwareKanban } =
		useEligibleForBasicSampleTasksForScrumOrSoftwareKanban();

	if (!userEligibleForBasicSampleTasksForScrumOrSoftwareKanban) {
		return {
			basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest: false,
			basicSampleTasksForScrumOrSoftwareKanbanFireExposure: undefined,
		};
	}

	const basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled =
		basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.type === 'enrolled';

	const basicSampleTasksForScrumOrSoftwareKanbanFireExposure =
		basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled
			? basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.fireExposure
			: undefined;

	return {
		basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest:
			basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled &&
			basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.value,
		basicSampleTasksForScrumOrSoftwareKanbanFireExposure,
	};
};

export const useIsSampleTasksForLandingGateNonTech = () => {
	const sampleTasksForLandingGateNonTechExperimentConfig = useExperiment(
		experiments.jfindSampleTasksForLandingGateNonTech,
	);

	const { userEligibleForSampleTasksForLandingGateNonTech } =
		useEligibleForSampleTasksForLandingGateNonTech();

	if (!userEligibleForSampleTasksForLandingGateNonTech) {
		return {
			sampleTasksForLandingGateNonTechIsEnrolledInTest: false,
			sampleTasksForLandingGateNonTechFireExposure: undefined,
		};
	}

	const sampleTasksForLandingGateNonTechIsEnrolled =
		sampleTasksForLandingGateNonTechExperimentConfig.type === 'enrolled';

	const sampleTasksForLandingGateNonTechFireExposure = sampleTasksForLandingGateNonTechIsEnrolled
		? sampleTasksForLandingGateNonTechExperimentConfig.fireExposure
		: undefined;

	return {
		sampleTasksForLandingGateNonTechIsEnrolledInTest:
			sampleTasksForLandingGateNonTechIsEnrolled &&
			sampleTasksForLandingGateNonTechExperimentConfig.value,
		sampleTasksForLandingGateNonTechFireExposure,
	};
};

export const useIsCodingAgentChoice = () => {
	const codingAgentChoiceExperimentConfig = useExperiment(experiments.twcg1935CodingAgentChoice);

	const userEligibleForCodingAgentChoice = useEligibleForTwcg1935CodingAgentChoice();

	if (!userEligibleForCodingAgentChoice) {
		return {
			codingAgentChoiceIsEnrolledInTest: false,
		};
	}

	const codingAgentChoiceIsEnrolled = codingAgentChoiceExperimentConfig.type === 'enrolled';

	// Exposure is fired automatically by the SDK (manifest
	// `fireExperimentExposure: true`), so we don't return a manual fire.
	return {
		codingAgentChoiceIsEnrolledInTest:
			codingAgentChoiceIsEnrolled && codingAgentChoiceExperimentConfig.value,
	};
};
